// src/components/ProfilePage.js
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import "./ProfilePage.css";

const ProfilePage = ({ onBack, onLogout }) => {
  const [user, setUser] = useState(null);
  const [passwordData, setPasswordData] = useState({ oldPassword: "", newPassword: "" });
  const [message, setMessage] = useState("");
  
  // Plan details state
  const [planDetails, setPlanDetails] = useState({
    planName: "Silver",
    purchaseDate: new Date().toISOString().split('T')[0],
    validityMonths: 3
  });

  // Support queries state
  const [supportQueries, setSupportQueries] = useState([
    { id: '#101954', type: 'Support call', status: 'In progress', date: new Date().toLocaleDateString() }
  ]);

  const supportContacts = {
    chat: { label: 'Chat with us', description: 'Get an instant reply', icon: '💬' },
    call: { label: 'Call us', value: '+91 9108000604', icon: '📞' },
    email: { label: 'Email us', value: 'support@stocks4u.com', icon: '✉️' }
  };

  // Calculate validity end date
  const calculateValidityEnd = () => {
    const start = new Date(planDetails.purchaseDate);
    const end = new Date(start);
    end.setMonth(end.getMonth() + planDetails.validityMonths);
    return end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Format purchase date
  const formatPurchaseDate = () => {
    const date = new Date(planDetails.purchaseDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Handle plan selection
  const handlePlanChange = (plan) => {
    const planConfig = {
      Diamond: { validityMonths: 12 },
      Gold: { validityMonths: 6 },
      Silver: { validityMonths: 3 }
    };
    setPlanDetails(prev => ({
      ...prev,
      planName: plan,
      validityMonths: planConfig[plan].validityMonths
    }));
  };

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user.email,
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();
      if (response.ok && data.status === "success") {
        setMessage("✅ Password updated successfully");
      } else {
        setMessage(data.message || "❌ Failed to update password");
      }
    } catch (err) {
      setMessage("Server error, try again later.");
    }
  };

  return (
    <div className="profile-page">
      <header className="oil-header">
        <div className="oil-header-container">
          <div className="logo">
          <img src="images/Final Font Stocks4U1.png" className="logo-text" />
          </div>
        </div>
      </header>
      {/* Top Bar */}
      <div className="profile-header s4u-title-row">
        <div className="s4u-page-heading">
          <button className="oil-back-btn s4u-back-btn" onClick={onBack}>
            ← Back to Dashboard
          </button>
          <h1>{user?.email?.split("@")[0]}</h1>
        </div>
      </div>

      <p className="welcome-text">Welcome Back, {user?.email?.split("@")[0]} 👋</p>

      <div className="profile-grid">
        {/* Left Side */}
        <div className="profile-left">
          {/* Personal + Buy Details */}
          <div className="details-section">
            <div className="personal-details">
              <h3>Personal Details</h3>
              <p>👤 Name: {user?.email?.split("@")[0] || "User"}</p>
              <p>📧 Email: {user?.email}</p>
              <p>📱 Phone: +91-XXXXXXXXXX</p>
            </div>

            <div className="buy-details">
              <h3>Buy Details</h3>
              <p><span className="dot purple"></span> <strong>Plan:</strong> {planDetails.planName}</p>
              <p><span className="dot blue"></span> <strong>Purchase Date:</strong> {formatPurchaseDate()}</p>
              <p><span className="dot red"></span> <strong>Valid Up To:</strong> {calculateValidityEnd()}</p>
              
              <div className="plan-selector">
                <label>Change Plan:</label>
                <select 
                  value={planDetails.planName} 
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="plan-dropdown"
                >
                  <option value="Silver">Silver (3 Months)</option>
                  <option value="Gold">Gold (6 Months)</option>
                  <option value="Diamond">Diamond (12 Months)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="security-section">
            <h3>Security</h3>
            <form onSubmit={handlePasswordChange} className="password-form">
              <input
                type="password"
                name="oldPassword"
                placeholder="Current Password"
                value={passwordData.oldPassword}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="newPassword"
                placeholder="New Password"
                value={passwordData.newPassword}
                onChange={handleChange}
                required
              />
              <button type="submit">Update Password</button>
            </form>
            {message && <p className="message">{message}</p>}
          </div>
        </div>

        {/* Right Side */}
        <div className="profile-right">
          <div className="current-plan">
            <h3>Current Plan: {planDetails.planName}</h3>
            <ul>
              <li>✔ Track Top Gainers & Losers</li>
              <li>✔ Comprehensive Company Insights</li>
              <li>✔ Global Market Coverage</li>
              {planDetails.planName === 'Gold' || planDetails.planName === 'Diamond' ? (
                <li>✔ Advanced Analytics</li>
              ) : null}
              {planDetails.planName === 'Diamond' ? (
                <li>✔ Priority Support & VIP Features</li>
              ) : null}
            </ul>
            <button className="upgrade-btn">Upgrade Plan</button>
          </div>

          {/* Customer Support Section */}
          <div className="customer-support">
            <h3>Customer support</h3>
            
            <div className="support-section">
              <h4>Active queries</h4>
              {supportQueries.map((query) => (
                <div key={query.id} className="support-query">
                  <span className="query-status">{query.status}</span>
                  <p className="query-type">{query.type}</p>
                  <p className="query-id">{query.id}</p>
                </div>
              ))}
            </div>

            <div className="support-section">
              <h4>Contact us 24x7</h4>
              <div className="support-contacts">
                <button className="support-btn" onClick={() => alert('Chat support coming soon!')}>
                  <span className="support-icon">{supportContacts.chat.icon}</span>
                  <div>
                    <p className="support-label">{supportContacts.chat.label}</p>
                    <p className="support-desc">{supportContacts.chat.description}</p>
                  </div>
                </button>
                
                <a href={`tel:${supportContacts.call.value}`} className="support-btn">
                  <span className="support-icon">{supportContacts.call.icon}</span>
                  <div>
                    <p className="support-label">{supportContacts.call.label}</p>
                    <p className="support-value">{supportContacts.call.value}</p>
                  </div>
                </a>
                
                <a href={`mailto:${supportContacts.email.value}`} className="support-btn">
                  <span className="support-icon">{supportContacts.email.icon}</span>
                  <div>
                    <p className="support-label">{supportContacts.email.label}</p>
                    <p className="support-value">{supportContacts.email.value}</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
